import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

/**
 * DeliveryTracking — public customer-facing tracking page.
 * Route: /track/:token  (token = delivery_stops.public_token)
 *
 * No authentication required. Backed by /api/delivery/track/:token which is
 * in the PUBLIC_PATHS allowlist.
 */
export default function DeliveryTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/api/delivery/track/${token}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  if (error)  return <Wrap><p>This tracking link is no longer valid.</p></Wrap>;
  if (!data)  return <Wrap><p>Loading…</p></Wrap>;

  const statusCopy = {
    scheduled: { title: 'Delivery scheduled', body: 'Your order is scheduled for local delivery. You\'ll see updates here once the driver is on the way.' },
    en_route:  { title: 'On the way', body: 'Your driver is headed to you. Live position updates below.' },
    arrived:   { title: 'Your driver has arrived', body: 'The driver is at your location.' },
    delivered: { title: 'Delivered', body: 'Your order has been delivered. Thank you!' },
    failed:    { title: 'Delivery unsuccessful', body: 'We weren\'t able to complete this delivery. Our team will reach out shortly.' },
    skipped:   { title: 'Rescheduled', body: 'This stop was rescheduled. You\'ll receive a new confirmation.' },
  };
  const copy = statusCopy[data.status] || statusCopy.scheduled;

  return (
    <Wrap>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 12, letterSpacing: 2, color: '#8B6F4E', textTransform: 'uppercase' }}>
          Daydreamer Delivery
        </div>
        <h1 style={{ margin: '12px 0 8px', fontSize: 26, fontWeight: 600 }}>{copy.title}</h1>
        <p style={{ color: '#666', fontSize: 15, marginTop: 0 }}>{copy.body}</p>
      </div>

      <div style={panelStyle}>
        <Row label="Order"     value={data.order_number} />
        <Row label="Recipient" value={data.customer_name} />
        <Row label="Address"   value={data.address} />
        {data.eta && <Row label="ETA" value={new Date(data.eta).toLocaleString()} />}
        {data.completed_at && <Row label="Delivered at" value={new Date(data.completed_at).toLocaleString()} />}
      </div>

      {data.status === 'en_route' && data.driver_position && (
        <div style={{ ...panelStyle, marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#8B6F4E', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
            Driver location
          </div>
          <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
            Last seen {new Date(data.driver_position.captured_at).toLocaleTimeString()}
          </div>
          {data.destination && (
            <a
              href={`https://www.google.com/maps/dir/${data.driver_position.lat},${data.driver_position.lng}/${data.destination.lat},${data.destination.lng}`}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-block', marginTop: 12, padding: '10px 16px',
                background: '#8B6F4E', color: '#fff', textDecoration: 'none',
                borderRadius: 6, fontWeight: 500,
              }}
            >
              View on map
            </a>
          )}
        </div>
      )}

      <div style={{ marginTop: 32, textAlign: 'center', fontSize: 12, color: '#999' }}>
        Daydreamer Concepts · 4500 Goer Dr, North Charleston, SC
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#FAFAF8', padding: '32px 16px',
      fontFamily: '-apple-system, Segoe UI, Helvetica, Arial, sans-serif', color: '#2b2b2b',
    }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0ea' }}>
      <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
      <span style={{ color: '#2b2b2b', fontSize: 14, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

const panelStyle = {
  background: '#fff', border: '1px solid #e5e5e0', borderRadius: 12, padding: 20,
};
